import { PHONE_STYLES } from './phone/styles-phone.js';

/**
 * The designer's own stylesheet, appended to the widget's `STYLES` and living
 * in the same shadow root — so a WooCommerce theme cannot restyle a handle and
 * the designer cannot leak a rule into the shop.
 *
 * Two rules shape everything here. First, phone first: the designer opens
 * full-screen inside a top-layer dialog, the canvas sits above a thumb-sized
 * tool bar, and tool panels are bottom sheets — never a 300 px side panel
 * squeezed onto a 360 px screen. Second, nothing is measured against the
 * window: the layout switches on the HOST's width (a class the module sets
 * from a ResizeObserver) and its height comes from the layer's own box, with
 * the visual-viewport custom properties as the ceiling. A 100vh designer is
 * a designer whose Gotovo button sits under the iOS toolbar.
 *
 * Class prefix is pzd- throughout, so it never collides with the form's pz-.
 * No backticks anywhere in this file: the whole sheet is a template literal.
 *
 * The phone shell keeps its rules in `phone/styles-phone.ts` and they are
 * appended at the bottom of `DESIGNER_STYLES`: one sheet reaches the shadow
 * root, whichever shell is mounted, and the layout can flip mid-session
 * without a second stylesheet having to be injected.
 */
const DESKTOP_STYLES = `
.pzd [hidden] { display: none !important; }
.pzd-command { min-height: 44px; padding: 10px; border: 1px solid #dedee9; background: #f7f7fb; color: #20202c; border-radius: 10px; font: inherit; cursor: pointer; }
.pzd-command:disabled { opacity: .45; cursor: default; }
.pzd-command-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin: 10px 0; }
.pzd-number-field { display: grid; grid-template-columns: minmax(0, 1fr) 84px; align-items: center; gap: 8px; margin: 12px 0; }
.pzd-number-field input, .pzd-template-search, .pzd-panel-body > select { min-height: 44px; width: 100%; min-width: 0; padding: 9px; border: 1px solid #dedee9; border-radius: 9px; font: inherit; background: #fff; color: #20202c; }
.pzd-selection-list { max-height: 190px; overflow: auto; margin: 12px 0; }
.pzd-selection-row { display: flex; align-items: center; min-height: 44px; gap: 10px; padding: 6px; }
.pzd-selection-row input { width: 20px; height: 20px; flex: 0 0 auto; }
.pzd-selection-row span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pzd-template-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
.pzd-template-card { border: 1px solid #dedee9; padding: 9px; border-radius: 12px; background: #fff; color: #20202c; font: inherit; cursor: pointer; text-align: left; overflow: hidden; }
.pzd-template-card canvas { width: 100%; height: 90px; object-fit: contain; display: block; background: #f7f6fa; border-radius: 6px; margin-bottom: 9px; }
.pzd-template-card small { display: block; color: #666575; margin-top: 5px; overflow-wrap: anywhere; }
.pzd-sticker-backing { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.pzd-sticker-status { position: absolute; bottom: 8px; left: 12px; right: 12px; padding: 7px 10px; background: #fffffff0; color: #444; font-size: 12px; border-radius: 8px; pointer-events: none; z-index: 8; }
.pzd.is-touch .pzd-icon-btn, .pzd.is-touch .pzd-lbtn { min-width: 44px; min-height: 44px; }
.pzd button:focus-visible, .pzd input:focus-visible, .pzd select:focus-visible { outline: 3px solid #3264ed; outline-offset: 2px; }
.pzd.is-phone input:not([type="checkbox"]):not([type="range"]):not([type="color"]),.pzd.is-phone textarea,.pzd.is-phone select{font-size:16px}
.pzd.is-phone .pzd-surface{min-height:44px}
@media (prefers-reduced-motion: reduce) { .pzd *, .pzd *::before, .pzd *::after { transition: none !important; animation: none !important; } }
/* ── Shell ──────────────────────────────────────────────────────────────── */
/* The root holds exactly one child — the mounted shell — plus the overlays,
   which are absolutely positioned inside it. */
.pzd { --pzd-line: #e4e4ed; --pzd-ink: #1b1b22; --pzd-dim: #71717d;
       --pzd-sel: #2f6bff; --pzd-bad: #c92a2a; --pzd-snap: #ff2fb3;
       --pzd-panel-w: 304px; --pzd-rail-w: 80px; --pzd-sheet-h: 56%;
       position: relative; display: grid; grid-template-rows: minmax(0, 1fr);
       height: 100%; min-height: 0; min-width: 0; overflow: hidden;
       background: #fff; color: var(--pzd-ink);
       font: var(--pz-font-size, 15px)/1.45 var(--pz-font, "Inter", "Segoe UI", system-ui, sans-serif);
       -webkit-user-select: none; user-select: none; touch-action: manipulation; }
.pzd *, .pzd *::before, .pzd *::after { box-sizing: border-box; }

.pzd-head { display: flex; align-items: center; gap: 10px; padding: 10px 12px;
            border-bottom: 1px solid var(--pzd-line); background: #fff; }
.pzd-head-status{display:flex;align-items:center;gap:4px}
.pzd.is-phone .pzd-head-copy{grid-template-columns:minmax(0,1fr) auto;align-items:center}
.pzd.is-phone .pzd-head-status{grid-column:2;grid-row:1}
.pzd.is-phone .pzd-surface{grid-column:1;grid-row:1;min-width:0}
.pzd.is-phone .pzd-head-actions{gap:4px;min-width:0}
.pzd-surface{max-width:100%;min-height:32px;font:inherit;font-size:12px;border:1px solid var(--pzd-line,#dce1e5);border-radius:6px;background:var(--pzd-panel,#fff)}
.pzd-head-copy { display: grid; gap: 1px; min-width: 0; flex: 1; }
.pzd-title { font-family: inherit; font-weight: 600; font-size: 15px; line-height: 1.2; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pzd-sub { font-size: 11.5px; color: var(--pzd-dim); min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pzd-head-actions { display: flex; align-items: center; gap: 6px; }
.pzd-chip { display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; border-radius: 99px;
            background: #f2f2f7; color: #4a4a57; font-size: 11.5px; white-space: nowrap; }
.pzd-chip.is-price { background: #eef0ff; color: #3b3a8f; font-weight: 600; }
.pzd-chip.is-problem { background: #fdecec; color: #a01b1b; }

.pzd-icon-btn { display: inline-grid; place-items: center; width: 38px; height: 38px; padding: 0;
                border: 1px solid transparent; border-radius: 11px; background: #f4f4f8;
                color: #3c3c48; cursor: pointer; transition: background .13s, color .13s, opacity .13s; }
.pzd-icon-btn:hover:not(:disabled) { background: #e9e9f2; }
.pzd-icon-btn:disabled { opacity: .36; cursor: default; }
.pzd-icon-btn.is-plain { background: transparent; }
.pzd-icon-btn svg { width: 20px; height: 20px; display: block; }
.pzd-primary { padding: 9px 18px; border: 0; border-radius: var(--pz-btn-radius, 99px);
               background: var(--pz-accent, #5145cd); color: var(--pz-btn-text, #fff);
               font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1; cursor: pointer; white-space: nowrap; }
.pzd-primary:hover { filter: brightness(1.06); }
.pzd-ghost { padding: 8px 14px; border: 1px solid var(--pzd-line); border-radius: 99px;
             background: #fff; color: #3c3c48; font-family: inherit; font-weight: 500; font-size: 13px; line-height: 1; cursor: pointer; }
.pzd-ghost:hover { background: #f6f6fa; }
.pzd :focus-visible { outline: 2px solid var(--pzd-sel); outline-offset: 2px; }

.pzd-body { position: relative; display: grid; min-height: 0; min-width: 0;
            grid-template-columns: var(--pzd-rail-w) var(--pzd-panel-w) minmax(0, 1fr); }
.pzd-body.no-panel { grid-template-columns: var(--pzd-rail-w) minmax(0, 1fr); }

/* ── Tool rail (desktop) / tool bar (phone) ─────────────────────────────── */
.pzd-rail { display: flex; flex-direction: column; gap: 2px; padding: 8px 6px;
            border-right: 1px solid var(--pzd-line); background: #fafafc; overflow: auto; }
.pzd-tool { display: grid; justify-items: center; gap: 3px; width: 100%; padding: 8px 2px;
            border: 0; border-radius: 12px; background: transparent; color: #55555f;
            font-family: inherit; font-weight: 500; font-size: 10.5px; line-height: 1.1; cursor: pointer; }
.pzd-tool svg { width: 22px; height: 22px; }
.pzd-tool:hover { background: #eeeef5; }
.pzd-tool.is-on { background: var(--pz-accent, #5145cd); color: #fff; }
.pzd-tool:disabled { opacity: .35; cursor: default; }

/* ── Tool panel (desktop) ──────────────────────────────────────────────── */
.pzd-panel { display: flex; flex-direction: column; min-height: 0;
             border-right: 1px solid var(--pzd-line); background: #fff; }
.pzd-panel-head { display: flex; align-items: center; gap: 8px; padding: 10px 12px 8px; }
.pzd-panel-head h3 { margin: 0; font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1.2; flex: 1; }
.pzd-panel-body { flex: 1; min-height: 0; overflow: auto; padding: 0 12px 16px;
                  -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
.pzd-grip { display: none; }

/* ── Canvas ─────────────────────────────────────────────────────────────── */
.pzd-main { position: relative; display: flex; min-width: 0; min-height: 0;
            background: repeating-conic-gradient(#eeeef3 0 25%, #f7f7fa 0 50%) 50%/16px 16px; }
.pzd-stage { position: relative; flex: 1; min-width: 0; min-height: 0; overflow: hidden; }
.pzd-ruler { position: absolute; background: rgba(255,255,255,.86); color: #7c7c88;
             pointer-events: none; z-index: 3; }
.pzd-ruler svg { display: block; }
.pzd-ruler-h { top: 0; height: 18px; border-bottom: 1px solid #e0e0ea; }
.pzd-ruler-v { left: 0; width: 18px; border-right: 1px solid #e0e0ea; }
.pzd-size-tag { position: absolute; z-index: 4; padding: 2px 7px; border-radius: 99px;
                background: rgba(20,20,32,.72); color: #fff; font-size: 10.5px; pointer-events: none; }

.pzd-zone { position: absolute; overflow: visible; background: #fff;
            box-shadow: 0 6px 26px rgba(20,20,40,.14); touch-action: none; }
.pzd-zone::after { content: ""; position: absolute; inset: 0; pointer-events: none;
                   border: 1.5px dashed #9a9aa8; }
.pzd-zone.is-dark { background: #16161c; }
.pzd-safe { position: absolute; pointer-events: none; border: 1px dashed rgba(120,120,140,.55); }
.pzd-zone.is-dark .pzd-safe { border-color: rgba(255,255,255,.4); }
.pzd-zone.is-dark::after { border-color: rgba(255,255,255,.5); }

.pzd-layer { position: absolute; left: 0; top: 0; transform-origin: 50% 50%;
             will-change: transform; }
.pzd-layer canvas, .pzd-layer img, .pzd-layer svg { display: block; width: 100%; height: 100%; }
.pzd-raster { position: absolute; }
.pzd-layer.is-hidden { display: none; }
.pzd-layer.is-outside::before { content: ""; position: absolute; inset: 0;
             outline: 2px solid var(--pzd-bad); outline-offset: 1px; pointer-events: none; }
/* DOM stand-ins, until the Skia bundle lands. */
.pzd-draft-text { position: absolute; inset: 0; display: grid; align-content: center;
                  white-space: pre; overflow: visible; line-height: 1.15; }
/* A fixed-width box: the stand-in wraps at the same column the renderer will. */
.pzd-draft-text.is-wrap { white-space: pre-wrap; overflow-wrap: break-word; word-break: break-word; }
.pzd-draft-img { position: absolute; inset: 0; object-fit: cover; }
.pzd-draft-art { position: absolute; inset: 0; object-fit: contain; }
.pzd-draft-missing { position: absolute; inset: 0; display: grid; place-items: center;
                     border-radius: 6px; background: rgba(140,140,160,.16); color: #6a6a78;
                     font-size: 11px; text-align: center; padding: 4px; }
.pzd-canvas.is-draft .pzd-layer { filter: saturate(.96); }

.pzd-empty { position: absolute; display: grid; place-content: center; justify-items: center;
             gap: 12px; padding: 16px; text-align: center; color: #6c6c7a; z-index: 2;
             pointer-events: none; }
.pzd-empty > * { pointer-events: auto; }
.pzd-empty strong { font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1.3; color: #3a3a46; }
.pzd-empty span { font-size: 12px; max-width: 26ch; }
.pzd-empty-row { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.pzd-empty-btn { display: grid; justify-items: center; gap: 4px; padding: 10px 14px;
                 border: 1px solid var(--pzd-line); border-radius: 13px; background: #fff;
                 color: #3c3c48; font-family: inherit; font-weight: 500; font-size: 12px; line-height: 1; cursor: pointer; }
.pzd-empty-btn svg { width: 22px; height: 22px; }
.pzd-empty-btn:hover { border-color: var(--pz-accent, #5145cd); color: var(--pz-accent, #5145cd); }

/* Selection chrome */
.pzd-sel { position: absolute; left: 0; top: 0; transform-origin: 50% 50%; pointer-events: none;
           z-index: 6; }
.pzd-sel::before { content: ""; position: absolute; inset: 0; border: 1.5px solid var(--pzd-sel); }
.pzd-sel.is-locked::before { border-style: dashed; border-color: #8a8a99; }
.pzd-handle { position: absolute; width: 22px; height: 22px; margin: -11px 0 0 -11px; padding: 0;
              border: 0; background: none; pointer-events: auto; cursor: pointer; touch-action: none; }
.pzd-handle::after { content: ""; position: absolute; inset: 6px; border-radius: 50%;
                     background: #fff; border: 1.5px solid var(--pzd-sel);
                     box-shadow: 0 1px 4px rgba(20,20,40,.25); }
.pzd-handle.is-side::after { inset: 7px 5px; border-radius: 4px; }
.pzd-handle.is-side.is-x::after { inset: 5px 7px; }
.pzd-handle.is-rotate::after { inset: 4px; background: var(--pzd-sel); border-color: #fff; }
.pzd-handle.is-rotate { cursor: grab; }
.pzd-sel-stem { position: absolute; width: 1.5px; background: var(--pzd-sel); }
.pzd-sel.is-locked .pzd-handle { display: none; }

.pzd-guide { position: absolute; background: var(--pzd-snap); pointer-events: none; z-index: 5; }
.pzd-guide.is-x { width: 1px; top: -10px; bottom: -10px; }
.pzd-guide.is-y { height: 1px; left: -10px; right: -10px; }

/* The floating mini toolbar: a white pill of three labelled buttons above the
   selection, the way the reference editor draws it. */
.pzd-actions { position: absolute; z-index: 7; display: flex; gap: 2px; padding: 3px;
               border-radius: 99px; background: #fff; box-shadow: 0 4px 18px rgba(10,10,20,.22); }
.pzd-pill-btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 11px;
                border: 0; border-radius: 99px; background: transparent; color: #33333f;
                font-family: inherit; font-weight: 500; font-size: 12px; line-height: 1; cursor: pointer; white-space: nowrap; }
.pzd-pill-btn:hover:not(:disabled) { background: #f1f1f7; }
.pzd-pill-btn:disabled { opacity: .35; cursor: default; }
.pzd-actions svg { width: 16px; height: 16px; }
.pzd.is-phone .pzd-pill-btn span { display: none; }
.pzd.is-phone .pzd-pill-btn { padding: 0 10px; height: 36px; }
.pzd-popover.is-fonts { width: 300px; height: 380px; padding: 0; }
.pzd-popover.is-fonts .pzd-fontpicker { position: relative; inset: auto; height: 100%; }
.pzd-sticker-status.is-bad { background: #fdecec; color: #8f1b1b; }

.pzd-layer-badge { position: absolute; left: 50%; bottom: -9px; transform: translate(-50%, 100%);
                   padding: 2px 7px; border-radius: 99px; font-size: 10px; white-space: nowrap;
                   background: var(--pzd-bad); color: #fff; z-index: 8; pointer-events: none; }
.pzd-layer-badge.is-warn { background: #b26a00; }

.pzd-backdrop-pick { position: absolute; right: 8px; bottom: 8px; z-index: 4; display: flex; gap: 4px;
                     padding: 4px; border-radius: 99px; background: rgba(255,255,255,.9);
                     box-shadow: 0 2px 10px rgba(20,20,40,.16); }
.pzd-backdrop-pick button { position: relative; width: 22px; height: 22px; padding: 0; border: 1.5px solid #d3d3de;
                            border-radius: 50%; cursor: pointer; }
.pzd-backdrop-pick button.is-on { border-color: var(--pz-accent, #5145cd); box-shadow: 0 0 0 2px rgba(81,69,205,.2); }
.pzd-backdrop-pick button.is-product { background-size: cover; background-position: center; background-color: #fff; }

/* Designing on the product: the picture sits under the zone, the zone paints
   nothing of its own, and its edge is drawn twice (light over dark) so it
   reads on a white shirt and on a navy one alike. */
.pzd-product-pic { position: absolute; z-index: 0; max-width: none; pointer-events: none; user-select: none;
                   -webkit-user-drag: none; }
.pzd-product-pic[hidden] { display: none; }
.pzd-stage.has-product { background: #ececf1; }
.pzd-zone.is-on-product { box-shadow: 0 0 0 1px rgba(20,20,32,.28); }
.pzd-zone.is-on-product::after { border: 1.5px dashed rgba(255,255,255,.85); }
/* The empty-zone words are dark grey; on a red or navy shirt they need a card. */
.pzd-stage.has-product .pzd-empty strong, .pzd-stage.has-product .pzd-empty span {
       padding: 2px 8px; border-radius: 8px; background: rgba(255,255,255,.9); }

/* ── Panel controls ────────────────────────────────────────────────────── */
.pzd-field { display: grid; grid-template-columns: minmax(0, 1fr); min-width: 0; gap: 6px; padding: 10px 0; border-bottom: 1px solid #f0f0f5; }
.pzd-field > * { min-width: 0; }
.pzd-field:last-child { border-bottom: 0; }
.pzd-field-head { display: flex; align-items: baseline; gap: 8px; }
.pzd-field-head label, .pzd-field-head span.pzd-lbl { font-family: inherit; font-weight: 600; font-size: 11.5px; line-height: 1.2; color: #5b5b67;
             letter-spacing: .02em; text-transform: uppercase; }
.pzd-field-val { margin-left: auto; font-size: 11.5px; color: var(--pzd-dim); font-variant-numeric: tabular-nums; }
.pzd-hint { font-size: 11.5px; color: var(--pzd-dim); }
.pzd-note { padding: 8px 10px; border-radius: 10px; background: #f6f6fa; font-size: 11.5px; color: #5b5b67; }
.pzd-note.is-bad { background: #fdecec; color: #8f1b1b; }
.pzd-note.is-warn { background: #fff5e6; color: #7d4e00; }

.pzd-textarea { width: 100%; min-height: 76px; padding: 10px 11px; border: 1.5px solid var(--pzd-line);
                border-radius: 11px; background: #fff; color: inherit; font: inherit; resize: vertical;
                -webkit-user-select: text; user-select: text; }
.pzd-textarea:focus { border-color: var(--pz-accent, #5145cd); outline: none; }
.pzd-input { min-height:44px; width: 100%; padding: 8px 10px; border: 1.5px solid var(--pzd-line); border-radius: 10px;
             background: #fff; color: inherit; font: inherit; -webkit-user-select: text; user-select: text; }

.pzd-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pzd-chip-btn { padding: 7px 11px; border: 1.5px solid var(--pzd-line); border-radius: 99px;
                background: #fff; color: #44444f; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1; cursor: pointer; }
.pzd-chip-btn:hover { border-color: #b9b9c8; }
.pzd-chip-btn.is-on { border-color: var(--pz-accent, #5145cd); background: #f0efff; color: #3b3a8f; }
.pzd-chip-btn svg { width: 16px; height: 16px; vertical-align: -3px; }

.pzd-swatches { display: grid; grid-template-columns: repeat(auto-fill, minmax(26px, 1fr)); gap: 6px; }
.pzd-swatch { position: relative; width: 100%; aspect-ratio: 1; padding: 0; border: 1.5px solid rgba(20,20,40,.14);
              border-radius: 50%; cursor: pointer; }
.pzd-swatch.is-on { box-shadow: 0 0 0 2px var(--pz-accent, #5145cd); }
.pzd-swatch.is-none { background: #fff; }
.pzd-swatch.is-none::after { content: ""; position: absolute; inset: 3px; border-radius: 50%;
              background: linear-gradient(to bottom right, transparent 44%, #c92a2a 44%, #c92a2a 56%, transparent 56%); }
.pzd-swatch.is-custom { background: conic-gradient(#ff2d55, #ffcc00, #34c759, #00c7be, #007aff, #af52de, #ff2d55); }
.pzd-color-native { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; }

.pzd-range { -webkit-appearance: none; appearance: none; width: 100%; height: 26px; background: transparent;
             cursor: pointer; }
.pzd-range::-webkit-slider-runnable-track { height: 4px; border-radius: 99px; background: #e4e4ee; }
.pzd-range::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; margin-top: -8px;
             border-radius: 50%; background: #fff; border: 1.5px solid var(--pz-accent, #5145cd);
             box-shadow: 0 1px 4px rgba(20,20,40,.22); }
.pzd-range::-moz-range-track { height: 4px; border-radius: 99px; background: #e4e4ee; }
.pzd-range::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: #fff;
             border: 1.5px solid var(--pz-accent, #5145cd); }

.pzd-switch { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 0; border: 0;
              background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.pzd-switch span { flex: 1; font-size: 13px; }
.pzd-switch i { width: 38px; height: 22px; flex: 0 0 auto; border-radius: 99px; background: #dcdce6;
                position: relative; transition: background .15s; }
.pzd-switch i::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
                border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(20,20,40,.3);
                transition: transform .15s; }
.pzd-switch[aria-pressed="true"] i { background: var(--pz-accent, #5145cd); }
.pzd-switch[aria-pressed="true"] i::after { transform: translateX(16px); }

.pzd-seg { display: flex; padding: 2px; border-radius: 10px; background: #f2f2f7; }
.pzd-seg button { flex: 1; padding: 7px 4px; border: 0; border-radius: 8px; background: transparent;
                  color: #55555f; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1; cursor: pointer; }
.pzd-seg button.is-on { background: #fff; color: var(--pzd-ink); box-shadow: 0 1px 3px rgba(20,20,40,.14); }
.pzd-seg svg { width: 16px; height: 16px; }

.pzd-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(62px, 1fr)); gap: 7px; }
.pzd-grid.is-fonts { grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); }
.pzd-tile { position: relative; display: grid; place-items: center; aspect-ratio: 1; padding: 6px;
            border: 1.5px solid var(--pzd-line); border-radius: 11px; background: #fff; cursor: pointer;
            overflow: hidden; }
.pzd-tile:hover { border-color: #b9b9c8; }
.pzd-tile.is-on { border-color: var(--pz-accent, #5145cd); background: #f6f5ff; }
.pzd-tile img, .pzd-tile svg { max-width: 100%; max-height: 100%; width: auto; height: auto; }
.pzd-tile.is-font { aspect-ratio: auto; min-height: 46px; padding: 6px 8px; }
.pzd-tile-name { font-size: 15px; line-height: 1.2; text-align: center; overflow: hidden;
                 text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.pzd-tile-sub { position: absolute; left: 0; right: 0; bottom: 0; padding: 1px 3px; font-size: 9px;
                color: #8a8a97; background: rgba(255,255,255,.86); text-align: center;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pzd-style-tile { min-height: 52px; aspect-ratio: auto; }
.pzd-style-preview { font-family: inherit; font-weight: 700; font-size: 24px; line-height: 1; }

.pzd-tabs { display: flex; gap: 5px; overflow-x: auto; padding: 2px 0 6px; scrollbar-width: thin; }
.pzd-tabs button { flex: 0 0 auto; padding: 6px 11px; border: 1.5px solid var(--pzd-line);
                   border-radius: 99px; background: #fff; color: #4a4a57; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1;
                   cursor: pointer; white-space: nowrap; }
.pzd-tabs button.is-on { border-color: var(--pz-accent, #5145cd); background: #f0efff; color: #3b3a8f; }
.pzd-search { display: flex; align-items: center; gap: 7px; padding: 7px 10px; border: 1.5px solid var(--pzd-line);
              border-radius: 11px; background: #fff; }
.pzd-search svg { width: 16px; height: 16px; color: #8a8a97; flex: 0 0 auto; }
.pzd-search input { flex: 1; min-width: 0; border: 0; background: none; font: inherit; color: inherit;
                    -webkit-user-select: text; user-select: text; }
.pzd-search input:focus { outline: none; }
.pzd-more { width: 100%; margin-top: 8px; padding: 9px; border: 1px solid var(--pzd-line);
            border-radius: 11px; background: #fff; color: #4a4a57; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1; cursor: pointer; }
.pzd-sentinel { height: 1px; }

.pzd-drop { display: grid; justify-items: center; gap: 8px; padding: 20px 14px; border: 1.5px dashed #c6c6d4;
            border-radius: 14px; background: #fafafd; color: #5b5b67; text-align: center; cursor: pointer; }
.pzd-drop.is-over { border-color: var(--pz-accent, #5145cd); background: #f3f2ff; }
.pzd-drop svg { width: 26px; height: 26px; color: #8a8a97; }
.pzd-drop strong { font-family: inherit; font-weight: 600; font-size: 13px; line-height: 1.2; color: #3a3a46; }
.pzd-drop span { font-size: 11.5px; }
.pzd-file { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }

.pzd-quality { display: inline-flex; align-items: center; gap: 6px; padding: 5px 9px; border-radius: 99px;
               font-size: 11.5px; }
.pzd-quality::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.pzd-quality.is-high { background: #e8f7ee; color: #17603a; }
.pzd-quality.is-mid { background: #eef3fd; color: #23497e; }
.pzd-quality.is-low { background: #fff5e6; color: #7d4e00; }

.pzd-crop { position: relative; aspect-ratio: 4 / 3; border-radius: 12px; overflow: hidden;
            background: #22222c; touch-action: none; }
.pzd-crop-control { width:100%; min-width:0; }
.pzd-crop-control .pzd-crop { max-height:220px; margin:auto; touch-action:none; cursor:move; }
.pzd-crop-flip { position:absolute; inset:0; transform-origin:center; }
.pzd-crop img { position: absolute; left: 50%; top: 50%; transform-origin: 50% 50%; max-width: none; }
.pzd-crop-frame { position: absolute; pointer-events: none; box-shadow: 0 0 0 9999px rgba(10,10,18,.5);
                  outline: 1.5px solid #fff; }

/* ── Layers list ───────────────────────────────────────────────────────── */
.pzd-list { display: grid; gap: 5px; }
.pzd-lrow { display: flex; align-items: center; gap: 8px; padding: 6px; border: 1.5px solid var(--pzd-line);
            border-radius: 11px; background: #fff; }
.pzd-lrow.is-on { border-color: var(--pz-accent, #5145cd); background: #f7f6ff; }
.pzd-lrow.is-drag { opacity: .5; }
.pzd-lrow.is-drop-before { box-shadow: 0 -3px 0 -1px var(--pz-accent, #5145cd); }
.pzd-lrow.is-drop-after { box-shadow: 0 3px 0 -1px var(--pz-accent, #5145cd); }
.pzd-lgrip { display: grid; place-items: center; width: 22px; height: 30px; color: #a6a6b4;
             cursor: grab; touch-action: none; flex: 0 0 auto; }
.pzd-lgrip svg { width: 15px; height: 15px; }
.pzd-lthumb { width: 34px; height: 34px; flex: 0 0 auto; display: grid; place-items: center;
              border-radius: 8px; background: repeating-conic-gradient(#eeeef3 0 25%, #f9f9fb 0 50%) 50%/8px 8px;
              overflow: hidden; color: #6a6a78; }
.pzd-lthumb img, .pzd-lthumb svg, .pzd-lthumb canvas { max-width: 100%; max-height: 100%; }
.pzd-lname { flex: 1; min-width: 0; padding: 0; border: 0; background: none; color: inherit; font: inherit;
             font-size: 13px; text-align: left; cursor: pointer; overflow: hidden; }
.pzd-lname b { display: block; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pzd-lname i { display: block; font-style: normal; font-size: 11px; color: var(--pzd-dim); }
.pzd-lrow.is-off .pzd-lname { opacity: .5; }
.pzd-lbtn { display: grid; place-items: center; width: 30px; height: 30px; padding: 0; border: 0;
            border-radius: 8px; background: transparent; color: #6a6a78; cursor: pointer; flex: 0 0 auto; }
.pzd-lbtn:hover { background: #f1f1f6; }
.pzd-lbtn.is-on { color: var(--pz-accent, #5145cd); }
.pzd-lbtn svg { width: 17px; height: 17px; }
.pzd-zone-thumb { display: grid; place-items: center; padding: 10px; border-radius: 12px;
                  background: repeating-conic-gradient(#eeeef3 0 25%, #f9f9fb 0 50%) 50%/12px 12px; }
.pzd-zone-thumb canvas { max-width: 100%; height: auto; box-shadow: 0 2px 10px rgba(20,20,40,.14); }

/* ── Phone: canvas on top, tool bar at the bottom, panels as sheets ────── */
.pzd.is-phone { --pzd-rail-w: 100%; }
.pzd.is-phone .pzd-body,
.pzd.is-phone .pzd-body.no-panel { grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto; }
.pzd.is-phone .pzd-main { grid-row: 1; }
.pzd.is-phone .pzd-rail { grid-row: 2; flex-direction: row; gap: 0; padding: 4px 4px calc(4px + env(safe-area-inset-bottom, 0px));
            border-right: 0; border-top: 1px solid var(--pzd-line); background: #fff; overflow-x: auto; }
/* The bar carries all six tools across the screen: the vertical rail's own
   full-width rule would make each one as wide as the bar and scroll the rest
   out of reach. */
.pzd.is-phone .pzd-tool { width: auto; flex: 1 1 0; min-width: 52px; padding: 7px 2px; border-radius: 10px; }
.pzd.is-phone .pzd-sub { display: none; }
.pzd.is-phone .pzd-title { font-size: 14px; }
/* The sheet stops at the top of the tool bar, whose height is measured into
   --pzd-bar-h: a sheet that covered the bar would hide the six buttons the
   shopper needs to get out of it. */
.pzd.is-phone .pzd-panel { position: absolute; left: 0; right: 0; bottom: var(--pzd-bar-h, 56px); z-index: 9;
            max-height: min(var(--pzd-sheet-h), 100%); height: auto;
            border-right: 0; border-top: 1px solid var(--pzd-line);
            border-radius: 18px 18px 0 0; box-shadow: 0 -8px 30px rgba(15,15,30,.2); }
.pzd.is-phone .pzd-panel-body { padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)); }
.pzd.is-phone .pzd-grip { display: block; width: 44px; height: 4px; margin: 8px auto 0; border-radius: 99px;
            background: #d8d8e2; }
.pzd.is-phone .pzd-head { padding: 8px; gap: 4px; }
.pzd.is-phone .pzd-head-actions { gap: 4px; }
.pzd.is-phone .pzd-chip { max-width: 74px; overflow: hidden; text-overflow: ellipsis; }
.pzd.is-phone .pzd-primary { padding: 9px 14px; }
/* The sheet must never stand under the on-screen keyboard. */
.pzd.has-keyboard .pzd-panel { max-height: 45%; }
.pzd.is-phone .pzd-handle { width: 30px; height: 30px; margin: -15px 0 0 -15px; }
.pzd.is-phone .pzd-actions button { width: 38px; height: 38px; }

/* Reserve real space for the sheet so the complete design stays visible. */
.pzd.is-phone .pzd-body:not(.no-panel) { grid-template-rows: minmax(0, 1fr) minmax(0, 48%) auto; }
.pzd.is-phone .pzd-body:not(.no-panel) .pzd-rail { grid-row: 3; }
.pzd.is-phone .pzd-panel { position: relative; grid-row: 2; bottom: auto; max-height: 100%; height: 100%; }
.pzd.is-phone .pzd-tool { min-width: 62px; font-size: 11px; }
@media (max-height: 500px) and (min-width: 600px) {
  .pzd.is-phone .pzd-body:not(.no-panel) { grid-template-columns: 260px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto; }
  .pzd.is-phone .pzd-body:not(.no-panel) .pzd-main { grid-column: 2; grid-row: 1; }
  .pzd.is-phone .pzd-body:not(.no-panel) .pzd-panel { grid-column: 1; grid-row: 1; border-radius: 0; }
  .pzd.is-phone .pzd-body:not(.no-panel) .pzd-rail { grid-column: 1 / -1; grid-row: 2; }
}

.pzd-main { flex-direction: column; }
.pzd-rail { overflow-x:hidden; scrollbar-width:thin; }
.pzd-tool { font:inherit; font-size:11px; line-height:1.2; font-weight:500; flex-shrink:0; min-width:0; }
.pzd-tool span { max-width:100%; overflow-wrap:anywhere; }
.pzd-zoom-bar { display:flex; align-items:center; justify-content:center; gap:4px; flex:none; padding:4px 8px; background:#fff; border-top:1px solid var(--pzd-line); }
.pzd-zoom-bar button { min-width:44px; min-height:44px; border:0; border-radius:8px; background:#f4f4f8; color:#39394b; font:inherit; cursor:pointer; }
.pzd-zoom-bar button[aria-pressed="true"] { background:#e6e2ff; color:#5145cd; }
.pzd-sticker-status { bottom:58px; }
.pzd-composition { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; }
.pzd-zone.has-composition .pzd-layer { opacity:0 !important; }
.pzd-zone.has-composition .pzd-sticker-backing { visibility:hidden; }
.pzd-canvas-grid { position:absolute; inset:0; pointer-events:none; background-image:linear-gradient(to right,#7180aa30 1px,transparent 1px),linear-gradient(to bottom,#7180aa30 1px,transparent 1px); }
.pzd-multi { position:absolute; inset:0; pointer-events:none; }
.pzd-multi-box { position:absolute; outline:1px solid var(--pzd-sel); }
.pzd-stage.is-panning,.pzd-stage.is-panning .pzd-zone { cursor:grab; touch-action:none; }
.pzd.is-review .pzd-safe,.pzd.is-review .pzd-guides,.pzd.is-review .pzd-sel,.pzd.is-review .pzd-actions,.pzd.is-review .pzd-sticker-status,.pzd.is-review .pzd-empty { display:none !important; }
.pzd.is-phone .pzd-head { flex-wrap:wrap; }
.pzd.is-phone .pzd-head-copy { flex-basis:100%; }
.pzd.is-phone .pzd-head-actions { width:100%; justify-content:flex-end; }
.pzd.is-dark-workspace { --pzd-ink:#f1f1f8; --pzd-dim:#b6b6ca; --pzd-line:#454552; background:#24242e; }
.pzd.is-dark-workspace .pzd-head,.pzd.is-dark-workspace .pzd-panel,.pzd.is-dark-workspace .pzd-rail,.pzd.is-dark-workspace .pzd-zoom-bar { background:#24242e; color:#f1f1f8; }
.pzd.is-dark-workspace .pzd-input,.pzd.is-dark-workspace .pzd-textarea,.pzd.is-dark-workspace select,.pzd.is-dark-workspace .pzd-number-field input{background:#33333f;color:#f1f1f8;border-color:#555564}
.pzd.is-dark-workspace .pzd-field-head label,.pzd.is-dark-workspace .pzd-lbl{color:#c7c7d7}
.pzd.is-dark-workspace .pzd-field{border-color:#454552}
.pzd.is-dark-workspace .pzd-icon-btn.is-plain{color:#e5e5f1}
.pzd.is-dark-workspace .pzd-main { background:#33333f; }
.pzd.is-dark-workspace .pzd-tool { color:#d5d5e5; }
.pzd.is-dark-workspace .pzd-tool.is-on { color:white; }
.pzd-panel-body input[type="file"] { max-width:100%; }
@media (max-height:500px) and (min-width:600px) { .pzd.is-phone .pzd-head { flex-wrap:nowrap; } .pzd.is-phone .pzd-head-copy { flex-basis:auto; } .pzd.is-phone .pzd-head-actions { width:auto; } }
@media (prefers-reduced-motion: reduce) {
  .pzd * { transition: none !important; }
}

/* ── Desktop shell ─────────────────────────────────────────────────────────
   The reference editor's frame: a 52 px header, a 44 px contextual toolbar
   that only exists with a selection, and a body of
   [rail | floating panel card | stage | side thumbnails]. */
/* Rows assigned explicitly for the same reason the columns are: the toolbar
   is display:none without a selection, and auto-placement would then pull the
   body up into its row and leave the stage short. */
.pzd-shell { display: grid; grid-template-rows: auto auto minmax(0, 1fr); height: 100%; min-height: 0; }
.pzd-shell.is-desktop > .pzd-head { grid-row: 1; }
.pzd-shell.is-desktop > .pzd-toolbar { grid-row: 2; }
.pzd-shell.is-desktop > .pzd-body { grid-row: 3; }
.pzd-shell.is-desktop .pzd-head { display: grid; align-items: center; gap: 12px; height: 52px; padding: 0 12px;
       grid-template-columns: auto minmax(0, 1fr) auto auto auto; }
.pzd-exit { display: inline-flex; align-items: center; gap: 7px; padding: 7px 10px; border: 0; border-radius: 10px;
       background: transparent; color: #45454f; font-family: inherit; font-weight: 500; font-size: 13px; line-height: 1; cursor: pointer; white-space: nowrap; }
.pzd-exit:hover { background: #f1f1f6; }
.pzd-exit-x { font-size: 15px; line-height: 1; }
.pzd-saved { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #6b6b78; white-space: nowrap; }
.pzd-saved .pzd-dot { width: 7px; height: 7px; border-radius: 50%; background: #3aa76d; }
.pzd-saved.is-pending .pzd-dot { background: #d9a400; }
.pzd-tabs.is-head { display: inline-flex; gap: 2px; padding: 3px; border-radius: 10px; background: #f2f2f7; overflow: visible; }
.pzd-tabs.is-head button { padding: 6px 16px; border: 0; border-radius: 8px; background: transparent;
       color: #55555f; font-family: inherit; font-weight: 600; font-size: 13px; line-height: 1; cursor: pointer; }
.pzd-tabs.is-head button.is-on { background: #fff; color: var(--pzd-ink); box-shadow: 0 1px 3px rgba(20,20,40,.14); }

.pzd-toolbar { display: flex; align-items: center; gap: 4px; min-height: 44px; padding: 0 8px;
       border-top: 1px solid var(--pzd-line); border-bottom: 1px solid var(--pzd-line); background: #fbfbfd; }
.pzd-shell.is-desktop > .pzd-toolbar[hidden] { display: flex !important; visibility: hidden; pointer-events: none; }
.pzd-toolbar-strip { display: flex; align-items: center; gap: 4px; flex: 1; min-width: 0; overflow-x: auto;
       scrollbar-width: none; scroll-behavior: smooth; }
.pzd-toolbar-strip::-webkit-scrollbar { display: none; }
.pzd-toolbar-more { flex: 0 0 auto; width: 30px; height: 30px; }
.pzd-tgroup { display: flex; align-items: center; gap: 2px; flex: 0 0 auto; padding-right: 6px;
       border-right: 1px solid #e8e8f0; }
.pzd-tgroup:last-child { border-right: 0; }
.pzd-tbtn { display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 5px 9px; border: 0;
       border-radius: 8px; background: transparent; color: #3c3c48; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1; cursor: pointer;
       white-space: nowrap; }
.pzd-tbtn:hover:not(:disabled) { background: #eeeef5; }
.pzd-tbtn:disabled { opacity: .38; cursor: default; }
.pzd-tbtn[aria-pressed="true"] { background: #e9e7ff; color: #3b3a8f; }
.pzd-tbtn.is-icon { padding: 5px; }
.pzd-tbtn svg { width: 17px; height: 17px; }
.pzd-tbtn.is-mirrored svg, .pzd-icon-btn.is-mirrored svg { transform: scaleX(-1); }
.pzd-tdot { width: 15px; height: 15px; border-radius: 50%; border: 1.5px solid rgba(20,20,40,.2); flex: 0 0 auto; }
.pzd-tdot.is-none { background: linear-gradient(to bottom right, transparent 44%, #c92a2a 44%, #c92a2a 56%, transparent 56%); }
.pzd-tchev svg { width: 13px; height: 13px; }
.pzd-tcount { padding: 0 8px; font-family: inherit; font-weight: 600; font-size: 12.5px; line-height: 1; color: #3b3a8f; white-space: nowrap; }
.pzd-toolbar .pzd-stepper { margin: 0; padding: 0; min-width: 132px; }
.pzd-toolbar .pzd-stepper .pzd-field-head { display: none; }

/* Five named columns, assigned explicitly: a hidden panel or review column is
   removed from the grid entirely, and auto-placement would then shift the
   stage into the slot the panel had left. */
.pzd-shell.is-desktop .pzd-body { grid-template-columns: 64px auto minmax(0, 1fr) auto auto; align-items: stretch; }
.pzd-shell.is-desktop > .pzd-body > .pzd-rail { grid-column: 1; }
.pzd-shell.is-desktop > .pzd-body > .pzd-panel { grid-column: 2; }
.pzd-shell.is-desktop > .pzd-body > .pzd-main { grid-column: 3; }
.pzd-shell.is-desktop > .pzd-body > .pzd-review { grid-column: 4; }
.pzd-options-slot { display: grid; gap: 14px; margin-top: 12px; }
.pzd-shell.is-desktop > .pzd-body > .pzd-sides { grid-column: 5; }
.pzd-shell.is-desktop .pzd-rail { width: 64px; gap: 2px; padding: 8px 4px; }
.pzd-shell.is-desktop .pzd-tool { padding: 9px 2px; font-size: 10.5px; }
.pzd-shell.is-desktop .pzd-panel { position: relative; width: 320px; margin: 12px 0 12px 12px;
       border: 1px solid var(--pzd-line); border-right: 1px solid var(--pzd-line); border-radius: 14px;
       box-shadow: 0 10px 34px rgba(20,20,40,.12); overflow: hidden; }
.pzd-shell.is-desktop .pzd-panel[hidden] { display: none; }
.pzd-sides { display: flex; flex-direction: column; gap: 8px; width: 96px; padding: 12px 8px;
       border-left: 1px solid var(--pzd-line); background: #fafafc; overflow: auto; }
.pzd-sides[hidden] { display: none; }
.pzd-side { display: grid; gap: 4px; padding: 5px; border: 1.5px solid transparent; border-radius: 10px;
       background: transparent; color: #4a4a57; font-family: inherit; font-weight: 500; font-size: 11px; line-height: 1.2; cursor: pointer; }
.pzd-side.is-on { border-color: var(--pz-accent, #5145cd); background: #fff; }
.pzd-side-thumb { height: 56px; border-radius: 7px; background: #fff; box-shadow: inset 0 0 0 1px #e2e2ec; }

.pzd-bottom { position: absolute; left: 12px; bottom: 12px; z-index: 9; display: flex; align-items: center; gap: 2px;
       padding: 4px; border-radius: 99px; background: #fff; box-shadow: 0 3px 16px rgba(20,20,40,.18); }
.pzd-bbtn { display: inline-flex; align-items: center; justify-content: center; min-width: 32px; height: 32px;
       padding: 0 8px; border: 0; border-radius: 99px; background: transparent; color: #45454f;
       font-family: inherit; font-weight: 600; font-size: 12px; line-height: 1; cursor: pointer; gap: 4px; }
.pzd-bbtn:hover { background: #f1f1f6; }
.pzd-bbtn.is-level { min-width: 66px; }
.pzd-bsep { width: 1px; height: 20px; background: #e4e4ee; margin: 0 3px; }

/* ── Review tab ────────────────────────────────────────────────────────── */
.pzd-review { width: 320px; padding: 16px; border-left: 1px solid var(--pzd-line); background: #fff; overflow: auto; }
.pzd-review[hidden] { display: none; }
.pzd-review-title { margin: 0 0 4px; font-family: inherit; font-weight: 600; font-size: 16px; line-height: 1.3; }
.pzd-checklist { display: grid; gap: 6px; margin: 14px 0; }
.pzd-check { display: flex; align-items: center; gap: 9px; width: 100%; padding: 9px 10px; border: 1.5px solid var(--pzd-line);
       border-radius: 11px; background: #fff; color: inherit; font-family: inherit; font-weight: 500; font-size: 12.5px; line-height: 1.3; text-align: left; cursor: pointer; }
.pzd-check.is-auto { cursor: default; }
.pzd-check-mark { display: grid; place-items: center; width: 20px; height: 20px; flex: 0 0 auto; border-radius: 50%;
       background: #ececf3; color: #9a9aa8; }
.pzd-check.is-on { border-color: #3aa76d; }
.pzd-check.is-on .pzd-check-mark { background: #e4f6ec; color: #17603a; }
.pzd-check.is-bad { border-color: var(--pzd-bad); }
.pzd-check.is-bad .pzd-check-mark { background: #fdecec; color: #a01b1b; }
.pzd-problems { display: grid; gap: 6px; margin: 8px 0 16px; }
.pzd-problem { display: flex; align-items: center; gap: 8px; padding: 9px 10px; border-radius: 11px;
       background: #fdecec; color: #8f1b1b; font-size: 12px; }
.pzd-problem button { margin-left: auto; flex: 0 0 auto; }
.pzd-primary.is-wide { width: 100%; }

/* ── Overlays: popovers and dialogs ────────────────────────────────────── */
.pzd-backdrop { position: absolute; inset: 0; z-index: 20; background: rgba(16,16,28,.42); }
.pzd-popover { position: absolute; z-index: 21; width: 268px; max-height: 70%; overflow: auto; padding: 10px 12px 12px;
       border-radius: 14px; background: #fff; box-shadow: 0 12px 40px rgba(16,16,32,.24); }
.pzd-popover.is-menu { width: 208px; padding: 5px; }
.pzd-popover.is-help { width: 290px; }
.pzd-popover.is-addtext { width: 300px; }
.pzd-dialog { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); z-index: 22;
       width: min(440px, calc(100% - 32px)); max-height: calc(100% - 48px); display: flex; flex-direction: column;
       border-radius: 16px; background: #fff; box-shadow: 0 20px 60px rgba(16,16,32,.32); }
.pzd-dialog.is-settings, .pzd-dialog.is-shortcuts { width: min(520px, calc(100% - 32px)); }
.pzd-dialog.is-preview { width: min(620px, calc(100% - 32px)); }
.pzd-over-head { display: flex; align-items: center; gap: 8px; padding: 12px 14px 6px; }
.pzd-over-head h4 { margin: 0; flex: 1; font-family: inherit; font-weight: 600; font-size: 15px; line-height: 1.2; }
.pzd-over-sub { margin: 18px 0 6px; font-family: inherit; font-weight: 600; font-size: 12px; line-height: 1.2; text-transform: uppercase; letter-spacing: .03em; color: #5b5b67; }
.pzd-over-body { padding: 0 14px 12px; overflow: auto; }
.pzd-popover > .pzd-over-body { padding: 0; }
.pzd-over-foot button { min-height: 44px; }
.pzd-over-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 10px 14px 14px; }
.pzd-menu-item { display: block; width: 100%; padding: 9px 11px; border: 0; border-radius: 9px; background: transparent;
       color: #35353f; font-family: inherit; font-weight: 500; font-size: 13px; line-height: 1; text-align: left; cursor: pointer; }
.pzd-menu-item:hover:not(:disabled) { background: #f2f2f8; }
.pzd-menu-item:disabled { opacity: .4; cursor: default; }
.pzd-menu-item.is-on { color: var(--pz-accent, #5145cd); font-weight: 600; }
.pzd-menu-sep { height: 1px; margin: 5px 8px; background: #ececf3; }
.pzd-setting { padding: 6px 0 10px; border-bottom: 1px solid #f2f2f7; }
.pzd-setting .pzd-hint { display: block; margin-top: -4px; }
.pzd-shortcuts { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 6px 16px; margin: 0; }
.pzd-shortcuts dt { font-size: 13px; color: #35353f; }
.pzd-shortcuts dd { margin: 0; font: 600 12px/1.4 ui-monospace, monospace; color: #55555f; white-space: nowrap; }
.pzd-preview-img { display: block; max-width: 100%; border-radius: 10px; }
.pzd-file-section { display: grid; gap: 8px; }
.pzd-file-section select, .pzd-file-section input[type="file"] { width: 100%; min-height: 40px; padding: 8px;
       border: 1px solid var(--pzd-line); border-radius: 9px; background: #fff; font: inherit; }

/* ── Stepper and colour block ──────────────────────────────────────────── */
.pzd-stepper { display: grid; gap: 6px; padding: 9px 0; }
.pzd-stepper-row { display: flex; align-items: center; gap: 4px; }
.pzd-step-btn { width: 30px; height: 30px; flex: 0 0 auto; border-radius: 8px; background: #f2f2f7; border: 0;
       color: #45454f; cursor: pointer; }
.pzd-step-btn:disabled { opacity: .35; cursor: default; }
.pzd-step-input { flex: 1; min-width: 44px; height: 30px; padding: 0 6px; border: 1.5px solid var(--pzd-line);
       border-radius: 8px; background: #fff; color: inherit; font: inherit; font-size: 13px; text-align: center;
       font-variant-numeric: tabular-nums; -webkit-user-select: text; user-select: text; }
.pzd-step-unit { font-size: 11.5px; color: var(--pzd-dim); }
.pzd-color-block { display: grid; gap: 7px; padding: 8px 0; }
.pzd-color-custom { display: grid; gap: 5px; }
.pzd-color-custom-row { display: flex; align-items: center; gap: 6px; }
.pzd-color-custom-row .pzd-input.is-hex { flex: 1; min-height: 32px; font-size: 12.5px; }
.pzd-input.is-bad { border-color: var(--pzd-bad); }
.pzd-icon-btn.is-drop { width: 32px; height: 32px; flex: 0 0 auto; }
.pzd-color-custom-row .pzd-swatch.is-custom { width: 30px; height: 30px; flex: 0 0 auto; aspect-ratio: auto; }
.pzd-swatches.is-dense { grid-template-columns: repeat(auto-fill, minmax(20px, 1fr)); gap: 4px; }
.pzd-color-extra { display: grid; gap: 6px; }
.pzd-disclose { display: flex; align-items: center; gap: 6px; width: 100%; padding: 8px 0; border: 0; background: none;
       color: #4a4a57; font-family: inherit; font-weight: 600; font-size: 11.5px; line-height: 1.2; text-transform: uppercase; letter-spacing: .02em; cursor: pointer; }
.pzd-disclose svg { transition: transform .15s; }
.pzd-disclosure.is-open > .pzd-disclose svg, .pzd-disclose.is-open svg { transform: rotate(180deg); }
.pzd-disclosure { border-bottom: 1px solid #f0f0f5; }
.pzd-disclosure-body { padding-bottom: 8px; }
.pzd-icon-row { display: flex; flex-wrap: wrap; gap: 4px; }
.pzd-reference { display: grid; gap: 4px; margin-top: 8px; }
.pzd-mode-tile { display: grid; justify-items: center; gap: 3px; min-width: 58px; padding: 7px 4px;
       border: 1.5px solid var(--pzd-line); border-radius: 10px; background: #fff; color: #4a4a57;
       font-family: inherit; font-weight: 500; font-size: 10.5px; line-height: 1.1; cursor: pointer; }
.pzd-mode-tile.is-on { border-color: var(--pz-accent, #5145cd); background: #f6f5ff; color: #3b3a8f; }
.pzd-tile-row { display: flex; flex-wrap: wrap; gap: 6px; }
/* "Moji dizajni" in the share dialog. */
.pzd-library { display: grid; gap: 8px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--pzd-line); }
.pzd-library-title { margin: 0; font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1.3; }
.pzd-library-save { display: flex; gap: 8px; }
.pzd-library-save .pzd-input { flex: 1; min-width: 0; }
.pzd-library-list { display: grid; gap: 6px; max-height: 260px; overflow: auto; }
.pzd-library-item { display: grid; grid-template-columns: 48px minmax(0, 1fr) auto auto; align-items: center; gap: 10px;
       padding: 6px; border: 1px solid var(--pzd-line); border-radius: 10px; }
.pzd-library-thumb { display: block; width: 48px; height: 48px; border-radius: 7px; overflow: hidden; background: #f0f0f5; }
.pzd-library-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pzd-library-text { display: grid; min-width: 0; }
.pzd-library-text b, .pzd-library-text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pzd-library-text small { color: #6d6d7a; }
a.pzd-command { text-decoration: none; }

/* Inline crop: the whole photo, dimmed outside the window, over the canvas. */
.pzd-icrop { position: absolute; inset: 0; z-index: 8; touch-action: none; cursor: move; }
.pzd-icrop-pic { position: absolute; max-width: none; pointer-events: none; user-select: none; opacity: .92; }
.pzd-icrop-frame { position: absolute; box-shadow: 0 0 0 100vmax rgba(18,18,28,.55); outline: 2px solid #fff;
       outline-offset: 0; }
.pzd-icrop-frame:focus-visible { outline-color: var(--pz-accent, #5145cd); }
.pzd-icrop-handle { position: absolute; width: 16px; height: 16px; margin: -8px 0 0 -8px; padding: 0; border: 2px solid #fff;
       border-radius: 4px; background: var(--pz-accent, #5145cd); box-shadow: 0 1px 4px rgba(0,0,0,.3); }
.pzd.is-touch .pzd-icrop-handle { width: 26px; height: 26px; margin: -13px 0 0 -13px; }
.pzd-icrop-handle[data-handle="nw"] { left: 0; top: 0; cursor: nwse-resize; }
.pzd-icrop-handle[data-handle="n"] { left: 50%; top: 0; cursor: ns-resize; }
.pzd-icrop-handle[data-handle="ne"] { left: 100%; top: 0; cursor: nesw-resize; }
.pzd-icrop-handle[data-handle="e"] { left: 100%; top: 50%; cursor: ew-resize; }
.pzd-icrop-handle[data-handle="se"] { left: 100%; top: 100%; cursor: nwse-resize; }
.pzd-icrop-handle[data-handle="s"] { left: 50%; top: 100%; cursor: ns-resize; }
.pzd-icrop-handle[data-handle="sw"] { left: 0; top: 100%; cursor: nesw-resize; }
.pzd-icrop-handle[data-handle="w"] { left: 0; top: 50%; cursor: ew-resize; }
.pzd-icrop-bar { position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%); z-index: 2; display: flex; gap: 8px;
       padding: 6px; border-radius: 99px; background: #fff; box-shadow: 0 4px 18px rgba(20,20,40,.25); cursor: default; }
.pzd-stage.is-inline-crop .pzd-sel, .pzd-stage.is-inline-crop .pzd-actions { display: none !important; }

/* Photo looks: this photo, once per look, in a three-column grid. */
.pzd-look-grid { position: relative; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
.pzd-look { display: grid; gap: 4px; padding: 4px; border: 1.5px solid transparent; border-radius: 10px;
       background: transparent; color: #3c3c48; font-family: inherit; font-weight: 500; font-size: 11px; line-height: 1.2; cursor: pointer; text-align: center; }
.pzd-look:hover { background: #f3f3f8; }
.pzd-look.is-on { border-color: var(--pz-accent, #5145cd); background: #f1f0ff; color: #3b3a8f; }
.pzd-look-thumb { display: block; aspect-ratio: 1; overflow: hidden; border-radius: 7px; background: #ececf2; }
.pzd-look-thumb img { display: block; width: 100%; height: 100%; object-fit: cover; }
.pzd-look-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pzd-quick-field { display: grid; gap: 5px; padding: 7px 0; }
.pzd-quick-head { display: flex; align-items: center; gap: 6px; }
.pzd-pill { padding: 2px 7px; border-radius: 99px; font-family: inherit; font-weight: 600; font-size: 10px; line-height: 1.4; }
.pzd-pill.is-must { background: #fdecec; color: #a01b1b; }
.pzd-textarea.is-short { min-height: 52px; }
.pzd-textarea.is-active { border-color: var(--pz-accent, #5145cd); }
.pzd-source-row { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0; }
.pzd-source-row .pzd-command.is-on { border-color: var(--pz-accent, #5145cd); background: #f0efff; color: #3b3a8f; }
.pzd-qr-box { display: grid; place-items: center; min-height: 160px; padding: 10px; border-radius: 12px; background: #f7f7fb; }
.pzd-qr-canvas { width: 180px; height: 180px; image-rendering: pixelated; }
.pzd-legend { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 11.5px; color: var(--pzd-dim); }
.pzd-legend-row { display: flex; align-items: flex-start; gap: 9px; padding: 7px 0; }
.pzd-legend-row strong { display: block; font-family: inherit; font-weight: 600; font-size: 12.5px; line-height: 1.3; }
.pzd-template-colors { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.pzd-template-colors button { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; }
.pzd-template-colors button[aria-pressed="true"] { outline: 2px solid var(--pz-accent); outline-offset: -2px; }
.pzd-template-colors button span { width: 20px; height: 20px; border-radius: 50%; border: 1px solid #0001; }
.pzd-legend-line { width: 22px; height: 0; flex: 0 0 auto; margin-top: 7px; border-top: 1px dashed #333; }
.pzd-legend-line.is-bleed { border-top-style: dashed; border-top-color: #9a9aa8; }
.pzd-legend-line.is-visible { border-top-style: solid; border-top-color: #9a9aa8; }
.pzd-legend-line.is-safe { border-top-style: dotted; border-top-color: #2f9e44; }

/* ── Font picker ───────────────────────────────────────────────────────── */
.pzd-fontpicker { position: absolute; inset: 0; z-index: 12; display: flex; flex-direction: column; gap: 8px;
       padding: 10px 12px 12px; background: #fff; }
.pzd-fontpicker .pzd-search { flex: 0 0 auto; }
.pzd-font-list { flex: 1; min-height: 0; overflow: auto; }
.pzd-font-row { display: flex; align-items: center; gap: 6px; }
.pzd-font-name { flex: 1; min-width: 0; padding: 10px 8px; border: 0; border-radius: 9px; background: none;
       color: inherit; font-size: 19px; line-height: 1.2; text-align: left; cursor: pointer; overflow: hidden;
       text-overflow: ellipsis; white-space: nowrap; }
.pzd-font-name:hover { background: #f4f4f9; }
.pzd-font-name.is-on { background: #f0efff; color: #3b3a8f; }
.pzd-font-more { width: 28px; height: 28px; flex: 0 0 auto; border: 0; border-radius: 8px; background: transparent;
       color: #7a7a88; cursor: pointer; }
.pzd-font-more.is-open svg { transform: rotate(180deg); }
.pzd-font-variants { display: flex; flex-wrap: wrap; gap: 5px; padding: 0 8px 8px 16px; }
.pzd-font-variant { padding: 5px 10px; border: 1px solid var(--pzd-line); border-radius: 99px; background: #fff;
       color: #4a4a57; font-family: inherit; font-weight: 500; font-size: 12px; line-height: 1; cursor: pointer; }
.pzd-font-variant.is-on { border-color: var(--pz-accent, #5145cd); background: #f0efff; color: #3b3a8f; }

/* ── Canvas additions: bleed, callouts, marquee, sticker pill ──────────── */
.pzd-bleed { position: absolute; z-index: 1; pointer-events: none; border: 1.5px dashed #9a9aa8; border-radius: 2px; }
.pzd-dim { position: absolute; z-index: 4; color: #8a8a97; font-size: 10.5px; pointer-events: none; }
.pzd-dim.is-h { display: flex; align-items: center; justify-content: center; }
.pzd-dim.is-h::before, .pzd-dim.is-h::after { content: ""; flex: 1; height: 1px; background: #c9c9d6; margin: 0 6px; }
.pzd-dim.is-v { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 20px;
       writing-mode: vertical-rl; }
.pzd-dim.is-v::before, .pzd-dim.is-v::after { content: ""; flex: 1; width: 1px; background: #c9c9d6; margin: 6px 0; }
.pzd-marquee { position: absolute; z-index: 8; pointer-events: none; border: 1.5px solid var(--pzd-sel);
       background: rgba(47,107,255,.1); }
.pzd-safe { border-style: dotted; border-color: #2f9e44; }
.pzd-zone::after { border-style: solid; border-color: #9a9aa8; border-width: 1px; }
.pzd-sticker-status { left: auto; right: 12px; bottom: 12px; width: max-content; max-width: 62%; }
.pzd .is-eyedropping, .pzd.is-eyedropping .pzd-stage { cursor: crosshair; }
.pzd-lpick { width: 16px; height: 16px; flex: 0 0 auto; }
.pzd-lgrip.is-empty { visibility: hidden; }
.pzd-lrow.is-background { background: #fafafc; }
`;

/**
 * One stylesheet for both shells. The phone rules are appended rather than
 * swapped in, so a window dragged across 860 px re-creates the shell without
 * touching the shadow root's `<style>`.
 */
export const DESIGNER_STYLES = `${DESKTOP_STYLES}\n${PHONE_STYLES}
.pzd-crop-stage { position:relative; margin:20px auto; max-height:300px; background:#24242b; touch-action:none; }
.pzd-crop-stage img { position:absolute; max-width:none; pointer-events:none; }
.pzd-crop-selection { position:absolute; border:2px solid white; cursor:move; touch-action:none; }
.pzd-crop-thirds { position:absolute; inset:0; pointer-events:none; background:linear-gradient(to right,transparent 33%,#fff7 33%,#fff7 33.4%,transparent 33.4%,transparent 66.6%,#fff7 66.6%,#fff7 67%,transparent 67%),linear-gradient(to bottom,transparent 33%,#fff7 33%,#fff7 33.4%,transparent 33.4%,transparent 66.6%,#fff7 66.6%,#fff7 67%,transparent 67%); }
.pzd-crop-handle { position:absolute; width:28px; height:28px; padding:0; border:0; background:transparent; transform:translate(-50%,-50%); touch-action:none; }
.pzd-crop-handle::after { content:''; position:absolute; inset:8px; background:#fff; border:1px solid #5146cf; border-radius:2px; }
.pzd-crop-handle[data-crop-handle=nw] {left:0;top:0;cursor:nwse-resize}
.pzd-crop-handle[data-crop-handle=n] {left:50%;top:0;cursor:ns-resize}
.pzd-crop-handle[data-crop-handle=ne] {left:100%;top:0;cursor:nesw-resize}
.pzd-crop-handle[data-crop-handle=e] {left:100%;top:50%;cursor:ew-resize}
.pzd-crop-handle[data-crop-handle=se] {left:100%;top:100%;cursor:nwse-resize}
.pzd-crop-handle[data-crop-handle=s] {left:50%;top:100%;cursor:ns-resize}
.pzd-crop-handle[data-crop-handle=sw] {left:0;top:100%;cursor:nesw-resize}
.pzd-crop-handle[data-crop-handle=w] {left:0;top:50%;cursor:ew-resize}
.pzd-crop-presets label {display:flex;align-items:center;gap:12px;font-size:14px}
.pzd-crop-presets select {flex:1;min-width:0;padding:10px;font-size:16px;border:1px solid #d4d4dc;border-radius:8px;background:var(--pzd-panel,#fff);color:inherit}
.pzd-dialog.is-crop {width:min(640px,calc(100vw - 24px))}

.pzd-layer.is-pattern { overflow:hidden; }
.pzd-layer.is-pattern > .pzd-raster, .pzd-layer.is-pattern > .pzd-draft-art { display:none; }
.pzd-pattern-tile { position:absolute; left:0; top:0; transform-origin:center; pointer-events:none; }
.pzd-pattern-tile img { position:absolute; max-width:none; transform-origin:center; }

.pzd-crop-image-viewport,.pzd-crop-shade {position:absolute;inset:0;overflow:hidden;pointer-events:none}
.pzd-crop-shade > div {position:absolute;box-shadow:0 0 0 9999px #0009}
.pzd.is-phone .pzd-crop-handle {width:44px;height:44px}
.pzd.is-phone .pzd-crop-handle::after {inset:16px}

.pzd-crop-presets {display:grid;gap:8px}
.pzd-crop-result {position:absolute;inset:0;overflow:hidden;pointer-events:none;background:repeating-conic-gradient(#eee 0% 25%,#fff 0% 50%) 0/12px 12px}
.pzd-crop-mask-preview {position:absolute;inset:0;mask-size:100% 100%;-webkit-mask-size:100% 100%;mask-repeat:no-repeat;-webkit-mask-repeat:no-repeat}
.pzd-crop-result img {position:absolute;max-width:none}

.pzd-dialog.is-eraser {width:min(820px,calc(100% - 24px));max-height:calc(100% - 24px)}
.pzd-template-preview {display:block;width:100%;max-height:45vh;object-fit:contain;background:repeating-conic-gradient(#eee 0% 25%,#fff 0% 50%) 0/16px 16px}
.pzd-erase-actions {display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px}
.pzd-erase-actions button[aria-pressed=true] {background:var(--pzd-accent,#292596);color:#fff}
.pzd-erase-viewport {overflow:auto;max-height:min(46vh,480px);border:1px solid #ccc;border-radius:8px;background:repeating-conic-gradient(#ddd 0% 25%,#fff 0% 50%) 0/16px 16px}
.pzd-erase-stage {position:relative;width:100%;margin-inline:auto}
.pzd-erase-stage canvas {display:block;width:100%;height:auto;touch-action:none;cursor:crosshair}
.pzd-erase-cursor {position:absolute;pointer-events:none;transform:translate(-50%,-50%);border:1px solid white;outline:1px solid #222;border-radius:50%}
.pzd.is-phone .pzd-dialog.is-eraser {width:calc(100% - 12px);max-height:calc(100% - 12px)}
.pzd.is-phone .pzd-erase-actions button {min-height:40px}

/* ── Quick start: three big choices on the empty zone ─────────────────── */
.pzd-empty strong { font-size: 16px; }
.pzd-start-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(86px, 1fr)); gap: 8px;
                 width: min(420px, calc(var(--pzd-empty-w, 320px) - 20px)); }
.pzd-start-card { display: grid; justify-items: center; align-content: center; gap: 4px; min-height: 108px;
                  padding: 12px 8px; border: 2px solid var(--pzd-line, #e4e4ed); border-radius: 18px;
                  background: #fff; color: #26262f; font-family: inherit; cursor: pointer; text-align: center;
                  box-shadow: 0 4px 14px rgba(20,20,40,.08); transition: transform .12s ease, border-color .12s ease; }
.pzd-start-card:hover, .pzd-start-card:focus-visible { border-color: var(--pz-accent, #5145cd); transform: translateY(-1px); }
.pzd-start-card:active { transform: scale(.97); }
.pzd-start-card strong { font-family: inherit; font-weight: 700; font-size: 15px; line-height: 1.2; color: #1f1f2a; }
.pzd-start-card small { font-size: 11.5px; line-height: 1.25; color: #6c6c7a; }
.pzd-start-art { display: inline-flex; align-items: center; gap: 2px; height: 40px; color: var(--pz-accent, #5145cd); }
.pzd-start-art svg { width: 30px; height: 30px; }
.pzd-start-art i { font-family: inherit; font-weight: 700; font-size: 16px; line-height: 1; font-style: normal; color: #9a9aa8; }
.pzd-start-card.is-photo-text { border-color: color-mix(in srgb, var(--pz-accent, #5145cd) 45%, #fff); }
.pzd-empty .pzd-empty-row { margin-top: 2px; }
.pzd-empty .pzd-empty-btn { grid-auto-flow: column; align-items: center; padding: 8px 12px; min-height: 40px; border-radius: 99px; }
.pzd-empty .pzd-empty-btn svg { width: 18px; height: 18px; }
.pzd-stage.has-product .pzd-empty > strong { padding: 3px 10px; border-radius: 10px; background: rgba(255,255,255,.92); }
.pzd-stage.has-product .pzd-start-card strong, .pzd-stage.has-product .pzd-start-card span,
.pzd-stage.has-product .pzd-empty-btn span { padding: 0; background: none; }

/* ── Busy veil (photo uploading) ──────────────────────────────────────── */
.pzd-busy { position: absolute; inset: 0; z-index: 40; display: grid; place-content: center; justify-items: center;
            gap: 12px; background: rgba(255,255,255,.72); color: #26262f; font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1.3;
            backdrop-filter: blur(2px); }
.pzd-spinner { width: 34px; height: 34px; border-radius: 50%; border: 3px solid #dcdcea;
               border-top-color: var(--pz-accent, #5145cd); animation: pzd-spin .8s linear infinite; }
@keyframes pzd-spin { to { transform: rotate(360deg); } }

/* ── "Upiši ime" ───────────────────────────────────────────────────────── */
.pzd-dialog.pzd-ask { width: min(440px, calc(100% - 24px)); }
.pzd-ask-input { display: block; width: 100%; min-height: 64px; padding: 12px 14px; border: 2px solid var(--pzd-line, #e4e4ed);
                 border-radius: 14px; background: #fff; color: #1f1f2a; font-family: inherit; font-weight: 600; font-size: 20px; line-height: 1.3; resize: none; }
.pzd-ask-input:focus { outline: none; border-color: var(--pz-accent, #5145cd); }
.pzd-dialog.pzd-ask .pzd-over-foot button { min-height: 48px; padding: 0 18px; font-size: 15px; }
/* On a phone the keyboard takes the lower half: the name field sits at the top. */
.pzd.is-phone .pzd-dialog.pzd-ask { top: 12px; transform: translateX(-50%); }

/* ── Rasporedi strip ───────────────────────────────────────────────────── */
.pzd-arrange { flex: none; display: grid; gap: 6px; padding: 8px 12px 10px; background: rgba(255,255,255,.96);
               border-top: 1px solid var(--pzd-line, #e4e4ed); min-width: 0; }
.pzd-arrange-head { display: flex; align-items: baseline; gap: 10px; min-width: 0; }
.pzd-arrange-head strong { font-family: inherit; font-weight: 700; font-size: 12.5px; line-height: 1.2; color: #26262f; }
.pzd-arrange-head .pzd-hint { margin: 0; font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pzd-arrange-row { display: flex; gap: 8px; overflow-x: auto; overflow-y: hidden; padding: 2px 2px 4px;
                   scroll-snap-type: x proximity; -webkit-overflow-scrolling: touch; scrollbar-width: thin; }
.pzd-arrange-card { flex: 0 0 auto; display: grid; justify-items: center; gap: 4px; width: 112px; padding: 5px;
                    border: 1.5px solid var(--pzd-line, #e4e4ed); border-radius: 12px; background: #fff;
                    color: #3c3c48; font-family: inherit; font-weight: 500; font-size: 11px; line-height: 1.2; cursor: pointer; scroll-snap-align: start; }
.pzd-arrange-card canvas { display: block; width: 100%; height: 64px; object-fit: contain; border-radius: 8px;
                           background: repeating-conic-gradient(#f1f1f5 0 25%, #fafafc 0 50%) 50%/10px 10px; }
.pzd-arrange-card span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pzd-arrange-card:hover { border-color: #b9b5ee; }
.pzd-arrange-card.is-on { border-color: var(--pz-accent, #5145cd); box-shadow: 0 0 0 2px rgba(81,69,205,.16); }
.pzd-arrange.is-compact { padding: 6px 8px 4px; gap: 4px; background: #fff; border-top: 0; border-bottom: 1px solid var(--pzd-line, #e4e4ed); }
.pzd-arrange.is-compact .pzd-arrange-card { width: 84px; padding: 4px; }
.pzd-arrange.is-compact .pzd-arrange-card canvas { height: 52px; }
.pzd-main.has-arrange > .pzd-bottom { bottom: calc(10px + var(--pzd-arrange-h, 132px)); }
.pzd-shell.is-desktop .pzd-arrange-card { width: 128px; }
.pzd-shell.is-desktop .pzd-arrange-card canvas { height: 72px; }
.pzd.is-review .pzd-arrange { display: none !important; }

/* ── Šabloni: kind tabs, colour dots, photo badge ─────────────────────── */
.pzd-template-kinds { margin: 4px 0 2px; }
.pzd-template-colors { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 4px; }
.pzd-colorway { width: 32px; height: 32px; min-height: 0 !important; padding: 0; border: 2px solid #fff; border-radius: 50%;
                box-shadow: 0 0 0 1px #d6d6e2; cursor: pointer; }
.pzd-colorway[aria-pressed="true"] { box-shadow: 0 0 0 2px var(--pz-accent, #5145cd); }
.pzd.is-touch .pzd-colorway { width: 40px; height: 40px; }
.pzd-template-card { position: relative; display: flex; flex-direction: column; }
.pzd-template-card:hover { border-color: #b9b5ee; }
.pzd-template-card canvas { height: 104px; }
.pzd-template-badge { position: absolute; top: 14px; left: 14px; padding: 3px 8px; border-radius: 99px;
                      background: rgba(20,20,32,.74); color: #fff; font-size: 10.5px; line-height: 1.2; pointer-events: none; }
`;
